import * as React from 'react';
import { Callout } from '@fluentui/react/lib/Callout';
import { IconButton } from '@fluentui/react/lib/Button';
import { useBoolean, useId } from '@fluentui/react-hooks';
import { Stack, IStackTokens } from '@fluentui/react/lib/Stack';
import { DirectionalHint, FontSizes, FontWeights, Label, mergeStyleSets, NeutralColors } from '@fluentui/react';

const stackTokens: IStackTokens = {
	childrenGap: 4,
	maxWidth: 330,
};

interface CustomLabelProps {
	description: string;
	iconTitle: string;
	iconAriaLabel: string;
	label?: string;
	required?: boolean;
	id?: string
}

export const CustomLabel = ({ label, required, description, id, iconTitle, iconAriaLabel }: CustomLabelProps): JSX.Element => {

	const descriptionId: string = useId('description');
	const iconButtonId: string = useId('iconButton');
	const labelId = useId('customLabel', id);

	const [isCalloutVisible, { toggle: toggleIsCalloutVisible }] = useBoolean(false);

	const style = React.useMemo(() => mergeStyleSets({
		label: {
			fontSize: FontSizes.size14,
			fontWeight: FontWeights.semibold,
			color: 'rgb(50, 49, 48)',
			marginRight: '-5px',
			'&:after': {
				content: required ? '" *"' : null,
				color: 'rgb(164, 38, 44)'
			}
		},
		description: {
			fontSize: FontSizes.size12,
			textAlign: 'center'
		}
	}), [required]);

	return (
		<>
			<Stack horizontal verticalAlign="center" tokens={stackTokens}>
				<Label className={style.label} id={`${labelId}-label`} required={required}>{label}</Label>
				<IconButton
					id={iconButtonId}
					iconProps={{ iconName: 'Info' }}
					title={iconTitle}
					ariaLabel={iconAriaLabel}
					onClick={toggleIsCalloutVisible}
					aria-haspopup={true}
					aria-expanded={isCalloutVisible}
					styles={{
						root: { marginBottom: -3 },
						icon: { color: NeutralColors.gray160 }
					}}
				/>
			</Stack>
			{isCalloutVisible && (
				<Callout
					target={`#${iconButtonId}`}
					setInitialFocus
					onDismiss={toggleIsCalloutVisible}
					ariaDescribedBy={descriptionId}
					directionalHint={DirectionalHint.topCenter}
					role="alert"
				>
					<Stack tokens={stackTokens} horizontalAlign="start" styles={{ root: { padding: '0.5rem' } }}>
						<span className={style.description} id={descriptionId}>{description}</span>
					</Stack>
				</Callout>
			)}
		</>
	);
};
